/**
 * Dashboard copy — §9 of `Brand_Design/myStack Dashboard - Engineering Handoff.html`.
 *
 * Every string the dashboard renders lives here, verbatim from the handoff.
 * They are in one module rather than inline so `copy.test.js` can assert the
 * whole surface against the spec in one place: §9 says "ship these strings
 * verbatim", and a string inlined in JSX is a string nobody diffs against the
 * document again.
 *
 * Voice rules for anything §9 does not list (also §9): sentence case except
 * eyebrow labels; no emoji; exact numbers in tabular figures; never the words
 * "proven", "guaranteed", "safe" or "working".
 */

import { MEMBERSHIP_LINE } from '../stack/SafetyNote';

/**
 * Which kind of interaction-check failure a card is reporting (STACK-542).
 *
 * The kind is decided once, in `Dashboard/index.js`, by the same
 * `isMembershipCheckError` predicate the stack page uses — whether the check
 * was skipped for a member the page already knows is lapsed, or actually fired
 * and was refused. The cards never re-derive it.
 */
export const INTERACTION_FAILURE_KIND = {
  MEMBERSHIP: 'membership',
  GENERIC: 'generic',
};

export const GREETING_SUBHEAD =
  'Curated recommendations based on your profile. Pin stacks that support your goals.';

/**
 * Shown instead of the default when the whole Recommended band is omitted —
 * §2's both-not-rendered row. It is the only way back, so it carries the route
 * rather than just acknowledging the empty state.
 */
export const GREETING_SUBHEAD_RECOVERY =
  'Update your profile to see recommended stacks.';

/**
 * The band's line for a lapsed member — it replaces the freshness line, in the
 * same slot directly under the "Recommended for you · Paused" label.
 *
 * It shipped under the page greeting first and was moved here on 2026-08-28
 * (Tanu): a line about the recommendations belongs with the recommendations,
 * not at the top of a page whose first section is My stacks. That is the same
 * reasoning that removed the default greeting subhead on 2026-08-27 — the
 * greeting is no longer adjacent to the band, so nothing that describes the
 * band can live there.
 *
 * It carries the freshness date because the line it replaces did. What it
 * drops is that line's "Update profile ›" link, which was competing with the
 * cards' own controls for the same click.
 */
export const PAUSED_LEAD = 'Your recommended stacks are waiting.';
export const PAUSED_UPDATED_PREFIX = 'Last updated';

/**
 * My stacks subhead for a lapsed member.
 *
 * Deliberately *not* "Still yours, still editable" — Tanu dropped that half on
 * 2026-08-28 because it overpromises: interaction checking is a membership
 * feature (`hasAccess('interaction_checking')` is false once lapsed), so a
 * lapsed member's own stacks are readable and editable but are no longer being
 * screened against their medications. Claiming "still editable" invites the
 * reading that nothing has changed about them, and something has.
 */
export const MY_STACKS_SUBHEAD_LAPSED =
  'A lapsed membership never locks your own stacks.';

export const SECTION_LABEL_RECOMMENDED = 'Recommended for you';
export const SECTION_HEADING_MY_STACKS = 'My stacks';
export const MY_STACKS_SUBHEAD = 'Stacks you built. Yours to edit, share or delete.';

export const FRESHNESS_PREFIX = 'Rebuilt whenever you update your medications or diet';
export const FRESHNESS_UPDATED_PREFIX = 'Last updated';
export const FRESHNESS_LINK = 'Update profile ›';

export const PIN_HELPER =
  'Pin to keep it in this row and check it against new medications';
export const PINNED_HELPER = 'Kept up to date with your profile. Unpin any time.';
export const PIN_ACTION = 'Pin';
export const PINNED_ACTION = 'Pinned';

/**
 * §3's overflow line, which Tanu ruled on 2026-08-26 is a *control*, not a
 * label: on Replenish and Nourish it expands the card to every supplement.
 * The collapsed string is §3's verbatim; `SHOW_FEWER` is new — §9 never
 * needed a second state because §3 treated this as text. Proposed, not ruled.
 */
export const overflowLabel = (n) => `+ ${n} more supplement${n === 1 ? '' : 's'}`;
export const SHOW_FEWER = 'Show fewer';

/** §7 / design-system brief: the copy inside the accent CTA while a stack builds. */
export const GENERATING = 'Generating your stack…';

/**
 * The page-foot medical disclaimer. Legal copy, ruled by Tanu on 2026-09-14
 * (STACK-541) — do not edit it for tone or length; changes go through the
 * maintainer. Shortened from 153 characters to 127 so it sets on one line at
 * desktop, where the old one wrapped inside the 820px foot block. It still
 * wraps on a phone, which is fine.
 */
export const DISCLAIMER =
  'This is not medical advice. Consult your healthcare provider before ' +
  'adding, stopping, or changing any supplement or medication.';

/**
 * Locked-card copy, per stack. `optOut` is the "Hide for now" line beneath the
 * hairline (state 4b); `optOutLink` is the part that is actually a control, so
 * the surrounding sentence is not a click target.
 */
export const LOCKED = {
  medication_support: {
    heading: 'Add your medications to unlock',
    body: 'Every suggestion is screened against what you already take.',
    cta: 'Add medications',
    optOutLead: 'Not taking any?',
    optOutLink: 'Hide for now',
    optOutTail: "— it's back when you add medications.",
  },
  diet_support: {
    heading: 'Set your diet to unlock',
    // The three names here are bound to SUPPORTED_DIETS in
    // `services/systemStackPrefs.js`, which is in turn bound to
    // DIET_TO_SUPPLEMENTS_MAPPING in `backend/endpoints/diet_stack.py`.
    // `copy.test.js` fails if a diet is added to one and not this sentence.
    body: 'Vegan, vegetarian and keto are supported today.',
    cta: 'Set diet',
    optOutLead: 'Diet not listed?',
    optOutLink: 'Hide for now',
    optOutTail: "— it's back when we support your diet.",
  },
};

/**
 * Paused-card copy, per stack — §2's "Lapsed members" row, state 4c.
 *
 * These are the render's own sentences, read off the 4c design Tanu supplied
 * on 2026-08-28. They replace the deleted `RecommendedStacksTab`'s lines,
 * which this shipped first because §9 lists no strings for this state and the
 * old tab was the only precedent. The render's are better: they say what
 * reactivating *does* ("rebuilt against your current medications") rather than
 * what the member is missing.
 *
 * **There is no heading.** An earlier revision added one, because a card with
 * its rows removed reads as a failed load. The render answers that differently
 * — the rows stay, blurred — so the heading is redundant and is gone.
 *
 * "Membership", never "Pro", in anything a user reads (repo `CLAUDE.md`).
 */
export const PAUSED_BADGE = 'Paused';

export const PAUSED = {
  medication_support: {
    body: 'Reactivate and your stack will be rebuilt against your current medications.',
    cta: 'Reactivate membership',
  },
  diet_support: {
    body: 'Reactivate and your stack will be rebuilt against your current diet.',
    cta: 'Reactivate membership',
  },
};

/**
 * The interaction check could not run — STACK-542.
 *
 * The defect this fixes is that a *failed* check looked exactly like a clean
 * one: `Dashboard/index.js` recorded only `VALID` and `PROCESSING`, so a
 * rejection left the card with whatever it had (usually nothing) and a member
 * read "no interactions found" off a check that never completed.
 *
 * Ruled by Tanu, 2026-09-14. Two properties are load-bearing and neither is
 * obvious from the string:
 *
 *   1. **"Try again" is words, not a control.** Nothing re-runs the check —
 *      the line tells the member the result is unknown, and a reload is how
 *      they retry. It is therefore rendered as muted text, never a button,
 *      and carries no green: green reads as an action on this surface.
 *   2. **A membership refusal is a different sentence**, and it is
 *      `MEMBERSHIP_LINE` from `stack/SafetyNote.js` rather than a second copy
 *      here. That line is already the stack page's answer for the same
 *      backend refusal (STACK-528); two independently-edited copies of one
 *      sentence is exactly how the disclaimer drifted into three slots.
 *
 * Note this sentence says "this stack", not "for interactions" — on the
 * dashboard the line sits on one card among several, so it has to say which
 * stack it failed for. `GENERIC_FAILURE_LINE` in `SafetyNote.js` is the stack
 * page's wording for the same state, where there is only one stack on screen.
 */
export const INTERACTION_CHECK_FAILED = "We couldn't check this stack. Try again";

/**
 * The one place a failure kind becomes a sentence.
 *
 * Both card types render this line, and the ticket's named silent failure is a
 * fix that lands on one of them and not the other. Resolving the string in one
 * helper both hosts call is what stops the two surfaces drifting — and it is
 * why the cards take the resolved line as a prop rather than mapping the kind
 * themselves.
 *
 * Returns `null` for a falsy kind, so "no failure recorded" and "render
 * nothing in that slot" are the same answer: a clean check and a still-running
 * check both leave `failureKind` unset.
 */
export const interactionFailureLine = (failureKind) => {
  if (!failureKind) return null;
  return failureKind === INTERACTION_FAILURE_KIND.MEMBERSHIP
    ? MEMBERSHIP_LINE
    : INTERACTION_CHECK_FAILED;
};

export const STACK_DISPLAY_NAME = {
  medication_support: 'Replenish Stack',
  diet_support: 'Nourish Stack',
};

/**
 * §9's toast row reads `{Stack} hidden — back when you add medications`.
 *
 * That tail is the *Replenish* promise; §9 lists one toast string for both
 * stacks, and applied literally it would tell a user who hid Nourish to add
 * medications, which is not what un-hides it. Each stack's own opt-out line
 * (above) already states its real condition, so the tail is taken from there.
 * Raised against the handoff rather than decided silently — see the PR's
 * Decisions table. `pinned` and `unpinned` ship exactly as §9 writes them.
 */
export const TOAST = {
  pinned: (stackName) => `${stackName} pinned`,
  unpinned: (stackName) => `${stackName} unpinned`,
  hidden: (stackName, stackType) =>
    stackType === 'diet_support'
      ? `${stackName} hidden — back when we support your diet`
      : `${stackName} hidden — back when you add medications`,
};

export const UNDO = 'Undo';
export const DISMISS_TOAST = 'Dismiss';

export const DELETE_DIALOG = {
  title: (stackName) => `Delete ${stackName}?`,
  body: (supplementCount) =>
    `${supplementCount} supplements. This can't be undone.`,
  cancel: 'Cancel',
  confirm: 'Delete stack',
};
